import React, {useCallback, useEffect, useState} from 'react';
import {Field, useForm, useFormState} from 'react-final-form';
import {useFieldArray} from 'react-final-form-arrays';
import block from 'bem-cn-lite';
import _ from 'lodash';

import {YCSelect} from '@yandex-data-ui/common';
import {FormFieldRadioButton, FormFieldText} from '@yandex-data-ui/cloud-components';

import {toBytes, fromBytes} from 'utils';
import {CalculatorProps} from 'components/calculator/models';
import {I18nFunction, I18nKFunction} from 'i18n';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';
import {FormFieldNumberInput} from 'components/formFields';
import CalculatorForm from 'components/calculator/CalculatorForm/CalculatorForm';
import ClosableSection from 'components/calculator/ClosableSection/ClosableSection';
import CalculatorFormLayout from 'components/calculator/CalculatorFormLayout/CalculatorFormLayout';
import AddButton from 'components/Buttons/AddButton/AddButton';
import TrafficField from 'components/calculator/Traffic/TrafficField/TrafficField';

import './StorageForm.scss';

const b = block('StorageForm');

const MAX_STORAGE_SIZE = toBytes(4000, 'tb');

const getClassOptions = _.memoize((i18nK: I18nKFunction) => [
    {content: i18nK('value_storage-type-standard'), value: 'STANDARD'},
    {content: i18nK('value_storage-type-cold'), value: 'COLD'},
]);

const getSizeUnitOptions = _.memoize((i18n: I18nFunction) => [
    {content: i18n('common', 'label_unit-gigabyte'), value: 'gb'},
    {content: i18n('common', 'label_unit-terabyte'), value: 'tb'},
]);

interface StorageFormItemValues {
    class: string;
    size: number;
    readOps: number;
    writeOps: number;
    traffic: number;
}

interface StorageFormValues {
    items: StorageFormItemValues[];
    [key: `${string}.size`]: number;
}

interface StorageFormFieldsProps {
    name: string;
    index: number;
    showClose: boolean;
    onClose: () => void;
    i18n: I18nFunction;
    i18nK: I18nKFunction;
}

const StorageFormFields: React.FC<StorageFormFieldsProps> = (props) => {
    const {name, index, showClose, onClose, i18n, i18nK} = props;

    const classOptions = getClassOptions(i18nK);
    const sizeUnitOptions = getSizeUnitOptions(i18n);

    const {change} = useForm<StorageFormValues>();
    const {
        values: {items},
    } = useFormState<StorageFormValues>();
    const [sizeUnit, setSizeUnit] = useState(sizeUnitOptions[0].value);

    const {size} = items[index];

    return (
        <CalculatorFormLayout.Section key={index}>
            <ClosableSection showClose={showClose} onClose={onClose}>
                <CalculatorFormLayout.Row
                    title={i18nK('field_storage-type')}
                    note={i18nK('context_storage-type')}
                >
                    <Field name={`${name}.class`}>
                        {(fieldProps) => (
                            <FormFieldRadioButton
                                {...fieldProps}
                                size="l"
                                controlClassName={b('storage-type')}
                                options={classOptions}
                                width="max"
                            />
                        )}
                    </Field>
                </CalculatorFormLayout.Row>
                <CalculatorFormLayout.Row
                    title={i18nK('field_storage-size')}
                    note={i18nK('context_storage-size')}
                >
                    <div className={b('size')}>
                        <Field
                            name={`${name}.size`}
                            format={(value) => String(fromBytes(value, sizeUnit))}
                            parse={(value) => {
                                const bytes = toBytes(Number(value), sizeUnit);
                                return bytes > MAX_STORAGE_SIZE ? MAX_STORAGE_SIZE : bytes;
                            }}
                        >
                            {(fieldProps) => (
                                <FormFieldText
                                    {...fieldProps}
                                    size="l"
                                    className={b('size-input')}
                                    accept={(value) => /^[0-9]*$/.test(value)}
                                />
                            )}
                        </Field>
                        <YCSelect
                            size="n"
                            className={b('size-select')}
                            value={sizeUnit}
                            showSearch={false}
                            onUpdate={(newSizeUnit) => {
                                const oldSize = fromBytes(size, sizeUnit);
                                const newSize = toBytes(oldSize, newSizeUnit);

                                const resultSize =
                                    newSizeUnit === 'tb' && newSize > MAX_STORAGE_SIZE
                                        ? MAX_STORAGE_SIZE
                                        : newSize;

                                change(
                                    `${name}.size`,
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    resultSize as unknown as any,
                                );

                                setSizeUnit(newSizeUnit);
                            }}
                            items={sizeUnitOptions.map(({content, value}) => ({
                                value,
                                title: content,
                            }))}
                        />
                    </div>
                </CalculatorFormLayout.Row>
                <CalculatorFormLayout.Row
                    title={i18nK('field_storage-read-ops')}
                    note={i18nK('context_storage-read-ops')}
                >
                    <Field name={`${name}.readOps`}>
                        {(fieldProps) => (
                            <FormFieldNumberInput
                                {...fieldProps}
                                step={100000}
                                className={b('read')}
                            />
                        )}
                    </Field>
                </CalculatorFormLayout.Row>
                <CalculatorFormLayout.Row
                    title={i18nK('field_storage-write-ops')}
                    note={i18nK('context_storage-write-ops')}
                >
                    <Field name={`${name}.writeOps`}>
                        {(fieldProps) => (
                            <FormFieldNumberInput
                                {...fieldProps}
                                step={100000}
                                className={b('write')}
                            />
                        )}
                    </Field>
                </CalculatorFormLayout.Row>
                <TrafficField name={`${name}.traffic`} />
            </ClosableSection>
        </CalculatorFormLayout.Section>
    );
};

type StorageFormProps = CalculatorProps & WithTranslationProps;

const StorageForm: React.FC<StorageFormProps> = (props) => {
    const {title, i18nK, i18n} = props;
    const {fields} = useFieldArray('items');
    const {initialize} = useForm<StorageFormValues>();
    const {initialValues} = useFormState<StorageFormValues>();

    const createItemDefaultData = useCallback((): StorageFormItemValues => {
        const classOptions = getClassOptions(i18nK);
        const sizeUnitOptions = getSizeUnitOptions(i18n);

        return {
            class: classOptions[0].value,
            size: toBytes(100, sizeUnitOptions[0].value),
            readOps: 0,
            writeOps: 0,
            traffic: 0,
        };
    }, [i18n, i18nK]);

    useEffect(() => {
        initialize({
            items: [createItemDefaultData()],
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!initialValues) {
        return null;
    }

    return (
        <CalculatorForm title={title} className={b()}>
            <CalculatorFormLayout>
                {fields.map((item, index) => (
                    <StorageFormFields
                        key={index}
                        i18n={i18n}
                        i18nK={i18nK}
                        index={index}
                        name={item}
                        showClose={Number(fields.length) > 1}
                        onClose={() => fields.remove(index)}
                    />
                ))}
                <CalculatorFormLayout.Section>
                    <AddButton onClick={() => fields.push(createItemDefaultData())}>
                        {i18nK('button_add-storage')}
                    </AddButton>
                </CalculatorFormLayout.Section>
            </CalculatorFormLayout>
        </CalculatorForm>
    );
};

export default withTranslation('calculator')(StorageForm);
